import { useNamedStore } from "@/lib/zustand/useNamedStore";
import type { Marker } from "@/types/defs/pitchCorrection/marker";
import type { Undoable } from "@/types/defs/pitchCorrection/undoable";

type State = {
  // the places in the pitch contour where we deem there is an important enough change to warrant a new "section". Sometimes this is a syllable, but more often than not it is a sub-syllable
  circles: Marker[];
  // the current state of pitches, should always be up to date is empty when we haven't received them from the server yet
  pitches: number[];
  // we use the original pitches from the server to always retain information about contour for certain algorithms also useful in a reset
  originalPitches: number[];
  // sometimes, we want to know if a pitch has been changed to know if doing certain operations on it makes sense for example, smoothing a contour to be more natural doesn't make sense if the pitch is distorted already beyond its natural state pitchesDirty tracks this
  pitchesDirty: boolean[];
  undoStack: Undoable[];
  redoStack: Undoable[];
  // updated pitch request takes the original pitch request as well as a diff of all of the pending pitch operations until the pitch request from the server resolves what this allows us to do, then is allow for full-featured knob moving even while we're waiting for the pitches to come back from the server as an example, let's say that somebody moves the vibrato knob while they're waiting for the pitch to come in initialPitchRequest = myPromise updatedPitchRequest = myPromise.then(doKnobManipulation)
  updatedPitchRequest: Promise<number[]>;
  // amount of cents by which to fuzz the pitch of the audio
  pitchFuzzInCents: number;
  // frequency of the time-fuzzing lfo
  timeFuzzInSeconds: number;
  vibratoLevel: number;
  pitchLevel: number;
  previousVibratoLevel: number;
  previousPitchLevel: number;
  previousVibratoPitches: number[] | null;
  previousPitchPitches: number[] | null;
  decodedAudioDataUrl: string | null;
  decimations: number[][];
  duration: number;
  processingCounter: number;
  // Identity of the file/session this store's state describes. unset() bumps it (a namespace store survives unset — the next file reuses it), so work captured under an older id can tell the store moved on: a closed editor's in-flight render used to land its audio URL here AFTER unset(), and the next file's editor then skipped its own initial render and played the previous file's audio.
  editingSessionId: number;
  reset: () => void;
  unset: () => void;
  setPitchFuzzInCents: (pitchFuzzInCents: number) => void;
  setTimeFuzzInSeconds: (timeFuzzInSeconds: number) => void;
  decreaseProcessingCounter: () => void;
  incraeseProcessingCounter: () => void;
  setDuration: (duration: number) => void;
  setCircles: (circles: Marker[]) => void;
  setPitches: (pitches: number[]) => void;
  setDecimations: (decimations: number[][]) => void;
  setDecodedAudioDataUrl: (decodedAudioDataUrl: string) => void;
  setPitchesDirty: (pitchesDirty: boolean[]) => void;
  setOriginalPitches: (pitches: number[]) => void;
  setUpdatedPitchRequest: (pitches: Promise<number[]>) => void;
  setVibratoLevel: (vibratoLevel: number) => void;
  setPitchLevel: (pitchLevel: number) => void;
  setPreviousVibratoLevel: (previousVibratoLevel: number) => void;
  setPreviousPitchLevel: (previousPitchLevel: number) => void;
  setPreviousVibratoPitches: (previousVibratoPitches: number[]) => void;
  unsetPreviousVibratoPitches: () => void;
  setPreviousPitchPitches: (previousPitchPitches: number[]) => void;
  unsetPreviousPitchPitches: () => void;
  unsetDecodedAudioDataUrl: () => void;
  addToUndoStack: (undoable: Undoable) => void;
  setUndoStack: (undoStack: Undoable[]) => void;
  setRedoStack: (redoStack: Undoable[]) => void;
};

const usePitchCorrectionInput = (
  set: (
    partial:
      | State
      | Partial<State>
      | ((state: State) => State | Partial<State>),
    replace?: boolean
  ) => void
): State => ({
  circles: [],
  originalPitches: [],
  pitches: [],
  pitchesDirty: [],
  vibratoLevel: 0,
  pitchLevel: 0,
  previousVibratoLevel: 0,
  duration: -1,
  previousPitchLevel: 0,
  previousVibratoPitches: null,
  previousPitchPitches: null,
  decodedAudioDataUrl: null,
  processingCounter: 0,
  editingSessionId: 0,
  decimations: [],
  undoStack: [],
  redoStack: [],
  updatedPitchRequest: new Promise(() => {}),
  pitchFuzzInCents: 0,
  timeFuzzInSeconds: 0,
  setPitchFuzzInCents: (pitchFuzzInCents: number) => set({ pitchFuzzInCents }),
  setTimeFuzzInSeconds: (timeFuzzInSeconds: number) =>
    set({ timeFuzzInSeconds }),
  incraeseProcessingCounter: () =>
    set(({ processingCounter }) => ({
      processingCounter: processingCounter + 1,
    })),
  decreaseProcessingCounter: () =>
    set(({ processingCounter }) => ({
      processingCounter: processingCounter > 0 ? processingCounter - 1 : 0,
    })),
  setDuration: (duration: number) => set({ duration }),
  setDecimations: (decimations: number[][]) => set({ decimations }),
  setDecodedAudioDataUrl: (decodedAudioDataUrl: string) =>
    set({ decodedAudioDataUrl }),
  unsetDecodedAudioDataUrl: () => set({ decodedAudioDataUrl: null }),
  setCircles: (circles: Marker[]) => set({ circles }),
  setPitches: (pitches: number[]) => set({ pitches }),
  setPitchesDirty: (pitchesDirty: boolean[]) => set({ pitchesDirty }),
  setOriginalPitches: (originalPitches: number[]) => set({ originalPitches }),

  setUpdatedPitchRequest: (updatedPitchRequest: Promise<number[]>) =>
    set({ updatedPitchRequest }),

  setVibratoLevel: (vibratoLevel: number) => set({ vibratoLevel }),
  setPitchLevel: (pitchLevel: number) => set({ pitchLevel }),
  setPreviousVibratoLevel: (previousVibratoLevel: number) =>
    set({ previousVibratoLevel }),
  setPreviousPitchLevel: (previousPitchLevel: number) =>
    set({ previousPitchLevel }),
  setPreviousVibratoPitches: (previousVibratoPitches: number[]) =>
    set({ previousVibratoPitches }),
  unsetPreviousVibratoPitches: () => set({ previousVibratoPitches: null }),
  setPreviousPitchPitches: (previousPitchPitches: number[]) =>
    set({ previousPitchPitches }),
  unsetPreviousPitchPitches: () => set({ previousPitchPitches: null }),
  addToUndoStack: (undoable: Undoable) =>
    set((state) => ({
      undoStack: [...state.undoStack, undoable],
    })),
  setUndoStack: (undoStack: Undoable[]) => set({ undoStack }),
  setRedoStack: (redoStack: Undoable[]) => set({ redoStack }),
  // The session id must move FORWARD through the reset (spreading the initial object would rewind it to 0, re-validating any render captured under the first session), so it is overridden after the spread.
  unset: () =>
    set((state) => ({
      ...usePitchCorrectionInput(set),
      editingSessionId: state.editingSessionId + 1,
    })),
  reset: () =>
    set(({ circles, originalPitches }) => ({
      circles,
      pitches: originalPitches,
      dirty: new Array(originalPitches.length).fill(false),
      undoStack: [],
      redoStack: [],
      vibratoLevel: 0,
      pitchLevel: 0,
      previousVibratoLevel: 0,
      previousPitchLevel: 0,
      previousVibratoPitches: null,
      previousPitchPitches: null,
      key: "No Key",
    })),
});

export const useNamedPitchCorrection = useNamedStore(
  () => usePitchCorrectionInput
);
